import { api } from '../../lib/api';
import { T, useGym } from '../../lib/gym';
import { useApi } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import { AsyncButton, Badge, Card, Empty, ErrorBox, Loading, PageHeader, useToast } from '../../ui/components';

/** Lessons and shifts colleagues handed back, plus the hours waiting for approval. */
export function SwapsPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const board = useApi(() => api<{ shifts: any[]; lessons: any[] }>(`${T(gym.slug)}/admin/swaps`), []);
  const pending = useApi(async () => (await api<{ items: any[] }>(`${T(gym.slug)}/admin/r/shifts`, {
    query: { pageSize: 100, sort: 'starts_at' },
  })).items.filter((s) => s.confirmed_at && !s.approved_at), []);

  if (board.error) return <div className="page"><ErrorBox error={board.error} onRetry={board.reload} /></div>;
  if (!board.data) return <Loading />;
  const empty = board.data.lessons.length === 0 && board.data.shifts.length === 0;

  return (
    <div className="page">
      <PageHeader title={i18n.t('swaps.title')} subtitle={i18n.t('swaps.subtitle')} />
      {empty && <Empty icon="🤝">{i18n.t('swaps.none')}</Empty>}

      {board.data.lessons.length > 0 && (
        <Card title={i18n.t('swaps.lessons')}>
          {board.data.lessons.map((l) => (
            <div key={l.id} className="row between wrap">
              <div>
                <strong>{l.lesson}</strong>
                <div className="small muted">{i18n.dateTime(l.starts_at)} · {l.location}</div>
              </div>
              <AsyncButton size="sm" onClick={async () => {
                await api(`${T(gym.slug)}/trainer/sessions/${l.id}/claim`, { body: {} });
                toast.ok(i18n.t('swaps.claimed'));
                board.reload();
              }}>{i18n.t('swaps.claim')}</AsyncButton>
            </div>
          ))}
        </Card>
      )}

      {board.data.shifts.length > 0 && (
        <Card title={i18n.t('swaps.shifts')}>
          {board.data.shifts.map((s) => (
            <div key={s.id} className="row between wrap">
              <div>
                <strong>{s.role || i18n.entity('shifts')}</strong>
                <div className="small muted">
                  {i18n.dateTime(s.starts_at)}–{i18n.time(s.ends_at)}{s.location ? ` · ${s.location}` : ''}
                  {s.offered_by ? ` · ${i18n.t('swaps.by', { name: s.offered_by })}` : ''}
                </div>
              </div>
              <AsyncButton size="sm" onClick={async () => {
                await api(`${T(gym.slug)}/admin/shifts/${s.id}/claim`, { body: {} });
                toast.ok(i18n.t('swaps.claimed'));
                board.reload();
              }}>{i18n.t('swaps.claim')}</AsyncButton>
            </div>
          ))}
        </Card>
      )}

      {(pending.data ?? []).length > 0 && (
        <Card title={i18n.t('hours.approve')} actions={
          <AsyncButton size="sm" onClick={async () => {
            const r = await api(`${T(gym.slug)}/admin/shifts/approve`, { body: { ids: pending.data!.map((s) => s.id) } });
            toast.ok(i18n.t('hours.approved', { count: r.approved }));
            pending.reload();
          }}>{i18n.t('hours.approve')}</AsyncButton>
        }>
          {pending.data!.map((s) => (
            <div key={s.id} className="row between wrap">
              <div className="small">{i18n.dateTime(s.starts_at)}–{i18n.time(s.ends_at)}{s.role ? ` · ${s.role}` : ''}</div>
              <Badge value="confirmed" label={i18n.t('hours.confirmed')} />
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

/** Certificates that must stay valid, soonest expiry first. */
export function QualificationsPage() {
  const gym = useGym();
  const i18n = useI18n();
  const list = useApi(() => api<any[]>(`${T(gym.slug)}/admin/qualifications`), []);
  if (list.error) return <div className="page"><ErrorBox error={list.error} onRetry={list.reload} /></div>;
  if (!list.data) return <Loading />;
  return (
    <div className="page">
      <PageHeader title={i18n.t('quals.title')} subtitle={i18n.t('quals.subtitle')} />
      {list.data.length === 0 ? <Empty icon="📄">{i18n.t('quals.none')}</Empty> : list.data.map((q) => (
        <Card key={q.id} className={`risk risk-${q.status === 'expired' ? 'high' : q.status === 'expiring' ? 'medium' : 'low'}`}>
          <div className="row between wrap">
            <div>
              <strong>{q.person}</strong> · {q.name}
              <div className="small muted">
                {q.expiresOn ? `${i18n.t('quals.expires', { date: i18n.day(q.expiresOn) })}${q.daysLeft !== null && q.daysLeft >= 0 ? ` · ${i18n.t('quals.days_left', { days: q.daysLeft })}` : ''}` : i18n.t('quals.no_date')}
                {q.issuer ? ` · ${q.issuer}` : ''}
              </div>
            </div>
            <div className="row gap-sm">
              {q.documentUrl && <a className="btn btn-ghost btn-sm" href={q.documentUrl} target="_blank" rel="noreferrer">{i18n.field('document_url')}</a>}
              <Badge value={q.status} label={i18n.t(`quals.status_${q.status}`)} />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
